import { StandardLonghandProperties } from 'csstype'
import { mapValues } from 'lodash/fp'

import { getPxInRem } from './styles'

export const defaultLineHeight = 1.4 // used in global styles

// consistent letter spacing sizes (in em)
export const letterSpacingValues = {
  tight: `-0.01em`,
  tighter: `-0.02em`,
  wide: `0.05em`,
} as const

// key of text letter spacing – use for prop specifying size of some basic text
export type TTextLetterSpacing = keyof typeof letterSpacingValues

// consistent font sizes (in rem or em)
const fontSizeValues = {
  text: {
    s12: `0.75`,
    s14: `0.875`,
    m16: `1`,
    m18: `1.125`,
    l20: `1.25`,
    l24: `1.5`,
  },
  header: {
    s14: `0.875`,
    m16: `1`,
    m18: `1.125`,
    l20: `1.25`,
    l22: `1.375`,
    l24: `1.5`,
    l28: `1.75`,
    l34: `2.125`,
    l50: `3.125`,
    l80: `5`,
  },
} as const

// values above transformed by adding the units (rem or em)
export const fontSizes = {
  text: mapValues((size) => `${size}rem`)(fontSizeValues.text) as typeof fontSizeValues.text,
  header: mapValues((size) => `${size}rem`)(fontSizeValues.header) as typeof fontSizeValues.header,
  em: {
    text: mapValues((size) => `${size}em`)(fontSizeValues.text) as typeof fontSizeValues.text,
    header: mapValues((size) => `${size}em`)(fontSizeValues.header) as typeof fontSizeValues.header,
  },
}

// key of text font sizes – use for prop specifying size of some basic text
export type TTextFontSize = keyof typeof fontSizeValues.text
// key of header font sizes – use for prop specifying size of some header text
export type THeaderFontSize = keyof typeof fontSizeValues.header

// define font names
//  - font should be registered (via @font-face) using this name
//  - fonts can be used as these constants (to prevent typos)
const interFontName = `Inter` as const

// any font family name – use for prop specifying font family
export type TFontFamily = typeof interFontName

// properties that the FontStyle combines
type TFontStyleSetting = {
  fontFamily: string
  casing?: StandardLonghandProperties['textTransform']
  fontWeight?: StandardLonghandProperties['fontWeight']
  fontStyle?: StandardLonghandProperties['fontStyle']
  letterSpacing?: StandardLonghandProperties['letterSpacing']
}

type TFontStyles = {
  light: TFontStyleSetting
  lightItalic: TFontStyleSetting
  regular: TFontStyleSetting
  regularItalic: TFontStyleSetting
  medium: TFontStyleSetting
  boldSemi: TFontStyleSetting
  bold: TFontStyleSetting
  boldExtra: TFontStyleSetting
  upperCase: TFontStyleSetting
}

const getFontFamilyFallbacks = (preferredFontFamily: TFontFamily) => {
  return `${preferredFontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif`
}

// use these font styles to keep consistency across the app
// define more if necessary
export const fontStyles: TFontStyles = {
  light: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    fontWeight: 300,
  },
  lightItalic: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    fontWeight: 300,
    fontStyle: `italic`,
  },
  regular: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    fontWeight: 400,
  },
  regularItalic: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    fontWeight: 400,
    fontStyle: `italic`,
  },
  medium: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    fontWeight: 500,
  },
  boldSemi: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    fontWeight: 600,
  },
  bold: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    fontWeight: 700,
  },
  boldExtra: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    fontWeight: 800,
    letterSpacing: letterSpacingValues.tighter,
  },
  upperCase: {
    fontFamily: getFontFamilyFallbacks(interFontName),
    letterSpacing: letterSpacingValues.wide,
    fontWeight: 600,
    casing: `uppercase`,
  },
} as const

// key of font styles – use for prop specifying style to be used in that component
export type TFontStyle = keyof typeof fontStyles

export const getTextFontSizeInPx = (size: TTextFontSize) => {
  const remSize = Number(fontSizeValues.text[size])
  const pxInRem = getPxInRem()

  return remSize * pxInRem
}

export const getHeaderFontSizeInPx = (size: THeaderFontSize) => {
  const remSize = Number(fontSizeValues.header[size])
  const pxInRem = getPxInRem()

  return remSize * pxInRem
}
